import { setFocusOnTv } from "@/utils/client.helpers";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "./Box";
import {
  BackspaceIcon,
  SearchIcon,
  SpaceIcon,
  ShiftOutlineIcon,
  ShiftLockedIcon,
  ShiftFilledIcon,
  TrashIcon,
} from "./Icons";
import { tvBackButtonOverrideHandler } from "@/utils/tv.helpers";
import { shoudlPreventResponsivenessOnLowerLg } from "@/utils/common.helpers";

enum ActionKeyValues {
  Space = "␣",
  Backspace = "backspace",
  ShowSpecialCharacters = "!#&",
  ShowAlphaNumeric = "abc 123",
  Clear = "Clear",
  Cancel = "Cancel",
  Done = "Done",
  Shift = "Shift",
}

enum ShiftState {
  OFF = "off",
  ON = "on",
  LOCKED = "locked",
}

// Define keyboard layout
const KEYBOARD_LAYOUT = {
  main: [
    ["a", "b", "c", "d", "e", "f"],
    ["g", "h", "i", "j", "k", "l"],
    ["m", "n", "o", "p", "q", "r"],
    ["s", "t", "u", "v", "w", "x"],
    ["y", "z", "1", "2", "3", "4"],
    ["5", "6", "7", "8", "9", "0"],
  ],
  specialCharacters: [
    ["!", "@", "#", "$", "%", "^"],
    ["&", "*", "(", ")", "_", "-"],
    ["+", "=", "[", "]", "{", "}"],
    ["|", "\\", ":", ";", '"', "'"],
    ["<", ">", "?", ".", "/", "`"],
    ["~", "`", "!", "@", "#", "$"],
  ],
  numeric: [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    [ActionKeyValues.Clear, "0", ActionKeyValues.Backspace],
  ],
};

interface TVKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface TvKeyboardKeyProps {
  value: string;
  onPress: (key: string, isLongPress?: boolean) => void;
  isFullWidth?: boolean;
  icon?: ({ fillColor }: { fillColor: "#fff" | "#000" }) => React.JSX.Element;
  displayText?: string;
  isActive?: boolean;
  numeric?: boolean;
  isLastRow?: boolean;
  onDownArrowFromLastRow?: () => void;
}

// Individual Key Component
const TvKeyboardKey: React.FC<TvKeyboardKeyProps> = ({
  value,
  onPress,
  isFullWidth,
  icon: Icon,
  displayText,
  numeric = false,
  isLastRow = false,
  onDownArrowFromLastRow,
}) => {
  // Long-press handling is derived from norigin-spatial-navigation's pressedKeys state.
  // We treat enter count > 1 as a "long press" signal and use a ref guard to avoid
  // firing multiple onPress events while the key is held down.
  const isLongPressRef = useRef(false);
  const { ref, focused } = useFocusable({
    focusKey: value,
    onArrowPress: (direction, a, b) => {
      // If down arrow is pressed from last row, move focus to button
      if (direction === "down" && isLastRow && onDownArrowFromLastRow) {
        onDownArrowFromLastRow();
        return false; // Prevent default navigation
      }
      return true;
    },
    onEnterPress: (e, d) => {
      // If we've already handled a long-press for this hold, ignore further press events
      if (isLongPressRef.current) {
        return;
      }
      // pressedKeys.enter increments while the button is held; >1 implies a long-press

      const isLongPress = d.pressedKeys.enter ? d.pressedKeys.enter > 1 : false;
      if (isLongPress) {
        // Latch until release so we don't double-trigger while held down
        isLongPressRef.current = true;
        setTimeout(() => {
          isLongPressRef.current = false;
        }, 500);
      }

      onPress(value, isLongPress);
    },
    onEnterRelease: () => {
      // Reset the guard on release so subsequent presses can be detected again
      isLongPressRef.current = false;
    },
  });

  const displayContent = Icon ? (
    <Icon fillColor={focused ? "#000" : "#fff"} />
  ) : (
    displayText || value
  );

  // Determine background color based on focused and active states
  const getBackgroundColor = () => {
    if (focused) return "bg-white text-black";
    return `${!numeric ? "bg-stg-grey-2 text-white" : ""}`;
  };

  const shouldPreventResponsivenessOnLowerLg =
    shoudlPreventResponsivenessOnLowerLg();

  return (
    <div
      ref={ref}
      className={`
        flex h-[50px] cursor-pointer items-center justify-center rounded-[4px] text-2xl font-medium
        !outline-none transition-all duration-150 ${!shouldPreventResponsivenessOnLowerLg ? "xl:h-[3.5rem] xl:text-2xl" : ""}
        ${getBackgroundColor()}
        ${isFullWidth ? "w-full" : `w-[3.125rem] ${!shouldPreventResponsivenessOnLowerLg ? "xl:w-[3.5rem]" : ""}`}
      `}
      onClick={() => onPress(value, false)}
    >
      {displayContent}
    </div>
  );
};

// Reusable rows renderer for keyboard layouts
const KeyboardRows: React.FC<{
  rows: string[][];
  onPress: (key: string, isLongPress?: boolean) => void;
  transformKey?: (key: string) => string;
  gapClassName: string;
  numeric?: boolean;
  onDownArrowFromLastRow?: () => void;
}> = ({
  rows,
  onPress,
  transformKey,
  gapClassName,
  numeric = false,
  onDownArrowFromLastRow,
}) => {
  const lastRowIndex = rows.length - 1;
  return (
    <>
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`grid ${numeric ? "grid-cols-[repeat(3,min-content)] justify-between" : "grid-cols-6"} ${gapClassName}`}
        >
          {row.map((key) => {
            const keyValue = transformKey ? transformKey(key) : key;
            const icon =
              key === ActionKeyValues.Clear
                ? TrashIcon
                : key === ActionKeyValues.Backspace
                  ? BackspaceIcon
                  : undefined;
            return (
              <TvKeyboardKey
                key={keyValue}
                value={keyValue}
                onPress={onPress}
                numeric={numeric}
                isLastRow={rowIndex === lastRowIndex}
                onDownArrowFromLastRow={onDownArrowFromLastRow}
                icon={icon}
              />
            );
          })}
        </div>
      ))}
    </>
  );
};

// Internal hook to set initial focus on first key

// Keyboard-only component (without input display)
export const TvVirtualKeyboard: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onCancel?: () => void;
  onDone?: () => void;
  showActionButtons?: boolean;
  numeric?: boolean;
  isFocusBoundary?: boolean;
  onDownArrowFromLastRow?: () => void;
}> = ({
  value,
  onChange,
  onCancel,
  onDone,
  showActionButtons = false,
  numeric = false,
  isFocusBoundary = true,
  onDownArrowFromLastRow,
}) => {
  const { ref: focusRef, focusKey } = useFocusable({
    isFocusBoundary,
  });
  const [showSpecialCharacters, setShowSpecialCharacters] = useState(false);
  const [shiftState, setShiftState] = useState<ShiftState>(ShiftState.OFF);

  const keyboardLayout = useMemo(() => {
    if (numeric) {
      return KEYBOARD_LAYOUT.numeric;
    }
    if (showSpecialCharacters) {
      return KEYBOARD_LAYOUT.specialCharacters;
    }
    return KEYBOARD_LAYOUT.main;
  }, [numeric, showSpecialCharacters]);

  useEffect(() => {
    const handleBackPress = () => {
      onCancel?.();
    };

    tvBackButtonOverrideHandler.set(handleBackPress);

    return () => {
      tvBackButtonOverrideHandler.clear();
    };
  }, []);

  // Focus on first key when keyboard opens
  useEffect(() => {
    // Add a small delay to ensure keyboard keys are registered with spatial navigation
    const timeoutId = setTimeout(() => {
      const firstKey = keyboardLayout[0][0];
      setFocusOnTv(firstKey);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [keyboardLayout]);

  const handleShiftPress = (isLongPress: boolean) => {
    if (isLongPress) {
      // Long press - lock shift
      if (shiftState !== ShiftState.LOCKED) {
        setShiftState(ShiftState.LOCKED);
      } else {
        setShiftState(ShiftState.OFF);
      }
    } else {
      if (shiftState === ShiftState.OFF) {
        // If off, single tap turns it on
        setShiftState(ShiftState.ON);
      } else {
        // If on, single tap turns it off
        setShiftState(ShiftState.OFF);
      }
    }
  };

  const handleKeyPress = (key: string, isLongPress?: boolean) => {
    switch (key) {
      case ActionKeyValues.Backspace:
        onChange(value.slice(0, -1));
        break;
      case ActionKeyValues.Space:
        onChange(value + " ");
        break;
      case ActionKeyValues.Clear:
        onChange("");
        break;
      case ActionKeyValues.Shift:
        // Shift key handled via long press detection
        handleShiftPress(isLongPress || false);
        break;
      case ActionKeyValues.ShowSpecialCharacters:
        setShowSpecialCharacters(!showSpecialCharacters);
        break;
      case ActionKeyValues.ShowAlphaNumeric:
        setShowSpecialCharacters(false);
        break;
      case ActionKeyValues.Cancel:
        onCancel?.();
        break;
      case ActionKeyValues.Done:
        onDone?.();
        break;
      default:
        if (shiftState === ShiftState.ON) {
          setShiftState(ShiftState.OFF);
        }

        onChange(value + key);
        break;
    }
  };

  // Get shift button display icon and active state
  const getShiftButtonDisplay = () => {
    switch (shiftState) {
      case ShiftState.LOCKED:
        return {
          icon: ShiftLockedIcon,
        }; // Filled arrow up with underscore
      case ShiftState.ON:
        return {
          icon: ShiftFilledIcon,
        };
      default:
        return {
          icon: ShiftOutlineIcon,
        }; // Outline arrow up
    }
  };

  const shiftButton = getShiftButtonDisplay();
  const gap = "gap-1 xl:gap-2";
  const numericGap = "!gap-4 !xl:gap-6";

  return (
    <FocusContext.Provider value={focusKey}>
      <Box ref={focusRef}>
        <div
          className={`grid grid-cols-1  ${numeric ? numericGap : gap} ${!numeric ? "bg-black px-2" : ""}  `}
        >
          {/* Regular keys */}
          <KeyboardRows
            rows={keyboardLayout}
            onPress={handleKeyPress}
            transformKey={(k) =>
              shiftState !== ShiftState.OFF ? k.toUpperCase() : k
            }
            gapClassName={numeric ? numericGap : gap}
            numeric={numeric}
            onDownArrowFromLastRow={onDownArrowFromLastRow}
          />

          {/* Special keys row - only show for non-numeric keyboards */}
          {!numeric && (
            <div className={`grid grid-cols-6 ${numeric ? numericGap : gap}`}>
              <TvKeyboardKey
                value={ActionKeyValues.Shift}
                onPress={(key, isLongPress) => {
                  handleShiftPress(isLongPress || false);
                }}
                icon={shiftButton.icon}
                numeric={numeric}
              />
              <div className="col-span-4">
                <TvKeyboardKey
                  value={ActionKeyValues.Space}
                  onPress={handleKeyPress}
                  isFullWidth
                  icon={SpaceIcon}
                  numeric={numeric}
                />
              </div>
              <div className="col-span-1">
                <TvKeyboardKey
                  value={ActionKeyValues.Backspace}
                  onPress={handleKeyPress}
                  icon={BackspaceIcon}
                  numeric={numeric}
                />
              </div>
            </div>
          )}

          {/* Special characters toggle and clear - only show for non-numeric keyboards */}
          {!numeric && (
            <div className={`grid grid-cols-2 ${gap}`}>
              <TvKeyboardKey
                value={ActionKeyValues.ShowSpecialCharacters}
                onPress={handleKeyPress}
                isFullWidth
                numeric={numeric}
              />
              <TvKeyboardKey
                value={ActionKeyValues.Clear}
                onPress={handleKeyPress}
                isFullWidth
                numeric={numeric}
              />
            </div>
          )}

          {/* Action buttons */}
          {showActionButtons && (
            <div className={`grid grid-cols-2 ${gap}`}>
              <TvKeyboardKey
                value={ActionKeyValues.Cancel}
                onPress={handleKeyPress}
                isFullWidth
                numeric={numeric}
              />
              <TvKeyboardKey
                value={ActionKeyValues.Done}
                onPress={handleKeyPress}
                isFullWidth
                numeric={numeric}
              />
            </div>
          )}
        </div>
      </Box>
    </FocusContext.Provider>
  );
};

// Full keyboard with input display
export const TvKeyboardWithInput: React.FC<TVKeyboardProps> = ({
  value,
  onChange,
  placeholder = "Search for...",
}) => {
  const { ref: focusRef, focusKey } = useFocusable();

  // Focus on first key when keyboard opens
  useEffect(() => {
    setFocusOnTv(KEYBOARD_LAYOUT.main[0][0]);
  }, []);

  const handleKeyPress = (key: string) => {
    switch (key) {
      case ActionKeyValues.Backspace:
        onChange(value.slice(0, -1));
        break;
      case ActionKeyValues.Space:
        onChange(value + " ");
        break;
      default:
        onChange(value + key);
    }
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <Box ref={focusRef}>
        <div className="grid grid-rows-1 gap-2">
          {/* Search Input Display */}
          <div className="flex w-full items-center">
            <div>
              <SearchIcon fillColor="stg-grey-4" width={24} height={24} />
            </div>
            <div className="flex w-full max-w-[258px] flex-1 items-center pl-2 text-lg text-white">
              <p
                className={`${
                  value ? "tv-keyboard-text-overflow" : ""
                } overflow-hidden xl:text-[1.375rem]`}
              >
                {value || placeholder}
              </p>
              {value && (
                <p className="tv-search-caret  inline-block h-5 w-[2px] bg-white" />
              )}
            </div>
          </div>

          {/* Main Keyboard Layout */}
          <div className="grid grid-cols-1 gap-[2px]">
            {/* Regular keys */}
            <KeyboardRows
              rows={KEYBOARD_LAYOUT.main}
              onPress={handleKeyPress}
              gapClassName={"gap-[2px]"}
            />

            {/* Bottom row with spacebar and back */}
            <div className="grid grid-cols-6 gap-[2px]">
              <div className="col-span-5">
                <TvKeyboardKey
                  value={ActionKeyValues.Space}
                  onPress={handleKeyPress}
                  isFullWidth
                  icon={SpaceIcon}
                />
              </div>
              <div className="col-span-1">
                <TvKeyboardKey
                  value={ActionKeyValues.Backspace}
                  onPress={handleKeyPress}
                  icon={BackspaceIcon}
                />
              </div>
            </div>
          </div>
        </div>
      </Box>
    </FocusContext.Provider>
  );
};
